import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import dimensions from '../dimensions';

export const MuiSelectTheme = (mode: PaletteMode): Components<Theme>['MuiSelect'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    defaultProps: {
      variant: 'outlined',
    },
    styleOverrides: {
      root: {
        // Base styles for Select matching Combobox behavior
        '&.MuiOutlinedInput-root': {
          // Outlined variant styling - matching pounce outline variant
          backgroundColor: componentTokens.inputs.colors.normal.background.resting,
          transition:
            'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          borderRadius: spacing(1.5),
          '& fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.resting,
            borderWidth: '1px',
            transition: 'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
            '& legend': {
              maxWidth: 0,
              visibility: 'hidden',
              width: 0,
            },
          },
          '&:hover': {
            backgroundColor: componentTokens.inputs.colors.normal.background.hover,
          },
          '&:hover fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.hover,
          },
          '&.Mui-focused': {
            backgroundColor: componentTokens.inputs.colors.normal.background.selected,
          },
          '&.Mui-focused fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.selected,
            borderWidth: '1px',
            '& legend': {
              maxWidth: 0,
              visibility: 'hidden',
              width: 0,
            },
          },
          '&.Mui-error': {
            backgroundColor: componentTokens.inputs.colors.error.background.resting,
          },
          '&.Mui-error fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.resting,
          },
          '&.Mui-error:hover': {
            backgroundColor: componentTokens.inputs.colors.error.background.hover,
          },
          '&.Mui-error:hover fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.hover,
          },
          '&.Mui-error.Mui-focused': {
            backgroundColor: componentTokens.inputs.colors.error.background.selected,
          },
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.selected,
          },
          '&.Mui-disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            backgroundColor: componentTokens.inputs.colors.normal.background.resting,
            '& fieldset': {
              borderColor: componentTokens.inputs.colors.normal.stroke.resting,
            },
          },
        },
        '.MuiOutlinedInput-input': {
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
          padding: '16px 50px 14px 14px',
        },
        '.MuiOutlinedInput-input.MuiSelect-multiple': {
          padding: '24.5px 14px 8.5px',
        },
      },
      select: {
        // Select input styling - matching pounce InputElement text color
        fontSize: typographyTokens.fontSizes.sm,
        fontWeight: typographyTokens.fontWeights.medium,
        color: componentTokens.inputs.colors.normal.text.inputText.resting,
        height: 'auto',
        lineHeight: 'normal',
        display: 'flex',
        alignItems: 'center',
        '&:focus': {
          backgroundColor: 'transparent',
          color: componentTokens.inputs.colors.normal.text.inputText.selected,
        },
        '&.Mui-disabled': {
          opacity: 0.1,
        },
        // Placeholder styling (when empty/no value selected)
        '&.MuiSelect-select:empty': {
          color: componentTokens.inputs.colors.normal.text.inputText.resting,
          opacity: 0.6,
        },
        // Also target when displayEmpty with empty value
        '&[data-value=""]': {
          color: componentTokens.inputs.colors.normal.text.inputText.resting,
          opacity: 0.6,
        },
      },
      icon: {
        // Dropdown icon styling
        color: componentTokens.inputs.colors.normal.assets.input.resting,
        right: spacing(2),
        pointerEvents: 'none',
      },
      // Styles for the native select (used as fallback)
      nativeInput: {
        opacity: 0,
        pointerEvents: 'none',
        position: 'absolute',
      },
    },
  };
};
